<template>
  <Container title="角色管理">
    <el-card shadow="never" class="search-card">
      <el-form :model="searchForm" label-position="left" label-width="70px" @submit.prevent @keyup.enter="handleSearch">
        <el-row :gutter="20">
          <el-col :xs="24" :sm="12" :md="6" :lg="6">
            <el-form-item label="角色名称">
              <el-input v-model="searchForm.name" placeholder="请输入角色名称" clearable style="width: 100%;" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="6" :lg="6">
            <el-form-item label="状态">
              <el-select v-model="searchForm.status" placeholder="请选择状态" clearable style="width: 100%;">
                <el-option label="启用" value="1" />
                <el-option label="停用" value="0" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6" style="display: flex; align-items: center; justify-content: flex-end;">
            <el-form-item label=" " label-width="0">
              <div style="display: flex; gap: 8px;">
                <el-button type="primary" @click="handleSearch">查询</el-button>
                <el-button @click="resetSearch">重置</el-button>
              </div>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
    </el-card>

    <el-card shadow="never" class="table-card">
      <template #header>
        <div class="table-header">
          <div class="table-header-left">
            <el-button type="primary" @click="openAddDialog"><el-icon><Plus /></el-icon> 新建角色</el-button>
          </div>
          <div class="table-header-right">
            <el-tooltip content="刷新" placement="bottom"><el-button circle @click="fetchData"><el-icon><Refresh /></el-icon></el-button></el-tooltip>
            <el-tooltip content="导出" placement="bottom"><el-button circle @click="handleExport"><el-icon><Download /></el-icon></el-button></el-tooltip>
            <el-tooltip content="打印" placement="bottom"><el-button circle @click="handlePrint"><el-icon><Printer /></el-icon></el-button></el-tooltip>
          </div>
        </div>
      </template>

      <el-table :data="tableData" border style="width: 100%">
        <el-table-column prop="id" label="ID" min-width="70" align="center" />
        <el-table-column prop="name" label="角色名称" min-width="140" show-overflow-tooltip />
        <el-table-column prop="description" label="描述" min-width="200" show-overflow-tooltip />
        <el-table-column label="用户数" min-width="100" align="center">
          <template #default="{ row }">
            <el-link type="primary" :underline="false" @click="openUserListDialog(row)" style="cursor: pointer; font-weight: 500;">
              {{ row.user_count || 0 }}
            </el-link>
          </template>
        </el-table-column>
        <el-table-column label="状态" min-width="100" align="center">
          <template #default="{ row }">
            <el-tag :type="row.status == 1 ? 'success' : 'danger'">{{ row.status == 1 ? '启用' : '停用' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="创建时间" min-width="160">
          <template #default="{ row }">{{ formatTime(row.created_at) }}</template>
        </el-table-column>
        <el-table-column label="操作" min-width="220" fixed="right" align="center">
          <template #default="{ row }">
            <el-button type="primary" size="small" plain @click="openEditDialog(row)">编辑</el-button>
            <el-button type="warning" size="small" plain @click="openPermissionDialog(row)">分配权限</el-button>
            <el-popconfirm confirm-button-text="删除" cancel-button-text="取消" title="确定删除该角色吗？" @confirm="handleDelete(row)">
              <template #reference>
                <el-button type="danger" size="small" plain>删除</el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-wrapper">
        <el-pagination
          v-model:current-page="page.current"
          v-model:page-size="page.pageSize"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="onSizeChange"
          @current-change="fetchData"
        />
      </div>
    </el-card>

    <!-- 用户列表弹窗 -->
    <el-dialog v-model="userListDialogVisible" :title="`角色「${currentRoleName}」下的用户`" width="600px" destroy-on-close>
      <el-table :data="roleUsers" border style="width: 100%">
        <el-table-column prop="account" label="账号" min-width="150" />
        <el-table-column prop="name" label="名称" min-width="150" />
        <el-table-column label="状态" min-width="100" align="center">
          <template #default="{ row }">
            <el-tag :type="row.status == 1 ? 'success' : 'danger'">{{ row.status == 1 ? '正常' : '停用' }}</el-tag>
          </template>
        </el-table-column>
      </el-table>
      <template #footer>
        <el-button @click="userListDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>

    <!-- 添加/编辑角色弹窗 -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="500px" @close="closeDialog" destroy-on-close>
      <el-form :model="form" :rules="rules" ref="formRef" label-width="100px" label-position="right">
        <el-form-item label="角色名称：" prop="name">
          <el-input v-model="form.name" placeholder="请输入角色名称" />
        </el-form-item>
        <el-form-item label="描述：">
          <el-input v-model="form.description" type="textarea" :rows="3" placeholder="请输入描述信息" />
        </el-form-item>
        <el-form-item label="状态：">
          <el-switch v-model="form.status" :active-value="1" :inactive-value="0" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="closeDialog">取消</el-button>
        <el-button type="primary" @click="submitForm" :loading="submitting">保存</el-button>
      </template>
    </el-dialog>

    <!-- 分配权限弹窗 -->
    <el-dialog v-model="permissionDialogVisible" title="分配菜单权限" width="700px" @close="closePermissionDialog" destroy-on-close>
      <div style="margin-bottom: 12px; display: flex; align-items: center; gap: 12px;">
        <el-button size="small" @click="checkAll">全选</el-button>
        <el-button size="small" @click="uncheckAll">全不选</el-button>
        <span style="margin-left: auto; color: #909399; font-size: 13px;">
          已选 {{ checkedCount }} 项
        </span>
      </div>
      <el-tree
        ref="permissionTreeRef"
        :data="permissionTreeData"
        show-checkbox
        node-key="id"
        :props="{ label: 'name', children: 'children' }"
        default-expand-all
        check-strictly
        :default-checked-keys="checkedPermissionIds"
        @check="handleTreeCheck"
      />
      <template #footer>
        <el-button @click="closePermissionDialog">取消</el-button>
        <el-button type="primary" @click="savePermissions" :loading="permSaving">保存权限</el-button>
      </template>
    </el-dialog>

    <ExportDialog
      v-model:visible="exportDialogVisible"
      :fields="exportFields"
      :export-api="exportApi"
      :get-search-params="getSearchParams"
      :current-page="page"
      default-file-name="角色数据"
    />
  </Container>
</template>

<script>
import { pub } from '/common/event';
import moment from 'moment';
import { Plus, Refresh, Download, Printer } from '@element-plus/icons-vue';
import ExportDialog from '@/components/container/ExportDialog.vue';
import exportMixin from '@/mixins/exportMixin';

export default {
  components: { Plus, Refresh, Download, Printer, ExportDialog },
  mixins: [exportMixin],
  data() {
    return {
      searchForm: { name: '', status: '' },
      tableData: [],
      total: 0,
      page: { current: 1, pageSize: 15 },

      dialogVisible: false,
      dialogTitle: '',
      editId: null,
      form: { name: '', description: '', status: 1 },
      rules: { name: [{ required: true, message: '请输入角色名称', trigger: 'blur' }] },
      submitting: false,

      permissionDialogVisible: false,
      currentRoleId: null,
      permissionTreeData: [],
      checkedPermissionIds: [],
      checkedCount: 0,
      permSaving: false,

      userListDialogVisible: false,
      currentRoleName: '',
      roleUsers: [],

      exportFields: [
        { key: 'id', label: '序号列', checked: true },
        { key: 'name', label: '角色名称', checked: true },
        { key: 'description', label: '描述', checked: true },
        { key: 'user_count', label: '用户数', checked: true },
        { key: 'status_label', label: '状态', checked: true },
        { key: 'created_at_label', label: '创建时间', checked: true },
      ],
    };
  },
  computed: {
    exportApi() {
      return this.$api.role.export;
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    handleExport() {
      this.openExportDialog();
    },
    formatTime(ts) {
      if (!ts) return '';
      return moment(ts).format('YYYY-MM-DD HH:mm:ss');
    },
    async fetchData() {
      try {
        const params = { ...this.searchForm, current: this.page.current, pageSize: this.page.pageSize };
        Object.keys(params).forEach(k => {
          if (params[k] === '' || params[k] === null || params[k] === undefined) delete params[k];
        });
        const ret = await this.$r.post(`role/role/page`, params);
        if (ret && ret.success) {
          this.tableData = ret.data.data || [];
          this.total = ret.data.total || 0;
          this.page.current = ret.data.page.current || this.page.current;
          this.page.pageSize = ret.data.page.pageSize || this.page.pageSize;
        }
      } catch (e) {
        pub('error_message', '加载角色列表失败');
      }
    },
    onSizeChange() {
      this.page.current = 1;
      this.fetchData();
    },
    handleSearch() {
      this.page.current = 1;
      this.fetchData();
    },
    resetSearch() {
      this.searchForm = { name: '', status: '' };
      this.page.current = 1;
      this.fetchData();
    },

    async openUserListDialog(row) {
      this.currentRoleName = row.name;
      this.roleUsers = [];
      this.userListDialogVisible = true;
      try {
        const ret = await this.$api.account.page({ role_id: row.id, current: 1, pageSize: 999 });
        if (ret && ret.success) {
          this.roleUsers = ret.data.data || [];
        } else {
          this.roleUsers = [];
        }
      } catch (e) {
        console.error('加载用户列表失败', e);
        this.roleUsers = [];
      }
    },

    openAddDialog() {
      this.editId = null;
      this.dialogTitle = '新建角色';
      this.form = { name: '', description: '', status: 1 };
      this.dialogVisible = true;
      this.$nextTick(() => this.$refs.formRef?.clearValidate());
    },
    openEditDialog(row) {
      this.editId = row.id;
      this.dialogTitle = '编辑角色';
      this.form = { ...row };
      this.dialogVisible = true;
      this.$nextTick(() => this.$refs.formRef?.clearValidate());
    },
    closeDialog() {
      this.dialogVisible = false;
      this.editId = null;
      this.form = { name: '', description: '', status: 1 };
    },
    async submitForm() {
      try { await this.$refs.formRef.validate(); } catch (e) { return; }
      this.submitting = true;
      try {
        const data = { ...this.form };
        let ret;
        if (this.editId) {
          ret = await this.$r.put(`role/role/${this.editId}`, data);
        } else {
          ret = await this.$r.post(`role/role`, data);
        }
        if (ret && ret.success) {
          pub('success_message', this.editId ? '编辑成功' : '创建成功');
          this.closeDialog();
          this.fetchData();
        } else {
          pub('error_message', ret?.message || '操作失败');
        }
      } catch (e) {
        pub('error_message', '请求失败');
      } finally {
        this.submitting = false;
      }
    },
    handleDelete(row) {
      this.$r.delete(`role/role/${row.id}`).then(ret => {
        if (ret && ret.success) {
          pub('success_message', '删除成功');
          this.fetchData();
        } else {
          pub('error_message', ret?.message || '删除失败');
        }
      });
    },

	async openPermissionDialog(row) {
	  this.currentRoleId = row.id;
	  this.permissionDialogVisible = true;
	  this.checkedPermissionIds = [];
	  this.checkedCount = 0;

	  try {
		const permRet = await this.$r.get(`account/account/permissions/list`);
		if (permRet && permRet.success) {
		  this.permissionTreeData = permRet.data.tree || [];
		}
	  } catch (e) {
		pub('error_message', '加载权限列表失败');
		return;
	  }

	  try {
		const roleRet = await this.$r.get(`role/role/${row.id}`);
		if (roleRet && roleRet.success) {
		  this.checkedPermissionIds = roleRet.data.permission_ids || [];
		  this.checkedCount = this.checkedPermissionIds.length;
		  
		  this.$nextTick(() => {
			const tree = this.$refs.permissionTreeRef;
			if (!tree) return;
			// 直接设置，不再过滤叶子节点
			tree.setCheckedKeys(this.checkedPermissionIds);
			this.checkedCount = tree.getCheckedKeys().length;
		  });
		}
	  } catch (e) {
		pub('error_message', '加载角色权限失败');
	  }
	},
    closePermissionDialog() {
      this.permissionDialogVisible = false;
      this.currentRoleId = null;
      this.checkedPermissionIds = [];
      this.checkedCount = 0;
    },
    handleTreeCheck() {
      this.$nextTick(() => {
        const checkedKeys = this.$refs.permissionTreeRef?.getCheckedKeys() || [];
        this.checkedCount = checkedKeys.length;
      });
    },
    checkAll() {
      const allIds = this.getAllNodeIds(this.permissionTreeData);
      this.$refs.permissionTreeRef?.setCheckedKeys(allIds);
      this.checkedCount = allIds.length;
    },
    uncheckAll() {
      this.$refs.permissionTreeRef?.setCheckedKeys([]);
      this.checkedCount = 0;
    },
    getAllNodeIds(treeData) {
      let ids = [];
      treeData.forEach(node => {
        ids.push(node.id);
        if (node.children && node.children.length) {
          ids = ids.concat(this.getAllNodeIds(node.children));
        }
      });
      return ids;
    },
    getLeafIdsOf(treeData) {
      const ids = [];
      const walk = (list) => {
        list.forEach(node => {
          if (!node.children || node.children.length === 0) ids.push(node.id);
          else walk(node.children);
        });
      };
      walk(treeData);
      return ids;
    },
    async savePermissions() {
	  if (!this.currentRoleId) return;
	  const tree = this.$refs.permissionTreeRef;
	  // 严格模式下，getCheckedKeys 获取的就是用户眼睛看到的打钩项
	  const permission_ids = tree?.getCheckedKeys() || [];

	  this.permSaving = true;
	  try {
		const ret = await this.$r.put(`role/role/${this.currentRoleId}/permissions`, { permission_ids });
		if (ret && ret.success) {
		  pub('success_message', '权限保存成功');
		  this.closePermissionDialog();
		  // ...
		}
	  } catch (e) {
		pub('error_message', '请求失败');
	  } finally {
		this.permSaving = false;
	  }
	},

    handlePrint() {
      window.print();
    },
  },
};
</script>

<style scoped>
.search-card { margin-bottom: 16px; }
.search-card .el-form-item { margin-bottom: 0; }
.table-card :deep(.el-card__header) { padding: 10px 16px; border-bottom: 1px solid #e4e7ed; }
.table-header { display: flex; justify-content: space-between; align-items: center; width: 100%; }
.table-header-left { display: flex; align-items: center; gap: 8px; }
.table-header-right { display: flex; align-items: center; gap: 4px; }
.pagination-wrapper { display: flex; justify-content: center; margin-top: 16px; }
</style>